import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Grid3X3, LayoutGrid, List, ChevronDown } from 'lucide-react';

interface CategoryHeaderProps {
  title: string;
  itemCount: number;
  sortBy: string;
  setSortBy: (value: string) => void;
  viewDensity: 'comfortable' | 'compact' | 'dense';
  setViewDensity: (density: 'comfortable' | 'compact' | 'dense') => void;
  isMobile: boolean;
}

const CategoryHeader = ({
  title,
  itemCount,
  sortBy,
  setSortBy,
  viewDensity,
  setViewDensity,
  isMobile
}: CategoryHeaderProps) => {
  const sortOptions = [
    { value: "name", label: "Name A-Z" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "featured", label: "Featured First" },
    { value: "newest", label: "Newest First" },
  ];

  const densityOptions = [
    { value: 'comfortable', icon: LayoutGrid, label: 'Comfortable' },
    { value: 'compact', icon: Grid3X3, label: 'Compact' },
    { value: 'dense', icon: List, label: 'Dense' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-subtle rounded-modern p-8 border border-border/50"
    >
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Title Section */}
        <div>
          <motion.h1 
            className="text-4xl lg:text-5xl font-playfair font-bold text-foreground mb-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {title}
          </motion.h1>
          <motion.p 
            className="text-muted-foreground text-lg"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
          </motion.p>
        </div>

        {/* Controls Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-background/80 backdrop-blur-sm border-border/50 hover:border-border transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background/95 backdrop-blur-sm border border-border/50">
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Density Controls - Desktop Only */}
          {!isMobile && (
            <div className="flex items-center gap-1 bg-background/50 rounded-lg p-1 border border-border/30">
              {densityOptions.map(({ value, icon: Icon, label }) => (
                <Button
                  key={value}
                  variant={viewDensity === value ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewDensity(value as any)}
                  className={`h-9 w-9 p-0 transition-all duration-200 ${
                    viewDensity === value 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={label}
                >
                  <Icon className="h-4 w-4" />
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Description (if available) */}
      {title !== 'All Categories' && (
        <motion.div 
          className="mt-6 pt-6 border-t border-border/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-muted-foreground leading-relaxed max-w-2xl">
            Discover our curated collection of premium {title.toLowerCase()} products. 
            Each item is carefully selected for quality, style, and exceptional value.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CategoryHeader;